import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  RotateCcw, 
  Edit, 
  Check, 
  X, 
  Package, 
  Calendar,
  User,
  FileText,
  Building2,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { retourService } from '../../services/retourService';
import { chantierService } from '../../services/chantierService';
import { productService } from '../../services/productService';
import { RetourChantier } from '../../types';

function RetourDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  
  const [retour, setRetour] = useState<RetourChantier | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadRetourData();
    }
  }, [id]);

  const loadRetourData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const retourData = await retourService.getRetour(id);
      setRetour(retourData);
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger les détails du retour',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleValidateRetour = async () => {
    if (!retour || !user) return;
    
    try {
      await retourService.validateRetour(retour.id, user.id);
      addNotification({
        userId: user.id,
        title: 'Retour validé',
        message: 'Le retour a été validé et le stock mis à jour',
        type: 'success'
      });
      loadRetourData();
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: 'Impossible de valider le retour',
        type: 'error'
      });
    }
  };

  const handleRejectRetour = async () => {
    if (!retour || !user) return;
    
    try {
      await retourService.rejectRetour(retour.id, user.id);
      addNotification({
        userId: user.id,
        title: 'Retour refusé',
        message: 'Le retour a été refusé',
        type: 'success'
      });
      loadRetourData();
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: 'Impossible de refuser le retour',
        type: 'error'
      });
    }
  };

  const canManageRetour = user && retour && (
    user.role === 'admin' || 
    user.role === 'responsable_depot' || 
    user.role === 'responsable_bureau'
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!retour) {
    return (
      <div className="text-center py-12">
        <RotateCcw className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Retour non trouvé</h3>
        <Link to="/retours" className="mt-2 text-blue-600 hover:text-blue-800">
          Retour à la liste
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg shadow-lg">
                <RotateCcw className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Retour #{retour.retourNumber}</h1>
                <p className="text-gray-600">{chantierService.getChantierName(retour.chantierId)}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {canManageRetour && retour.status === 'en_attente' && (
                <>
                  <button
                    onClick={handleValidateRetour}
                    className="btn-primary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
                  >
                    <Check className="h-4 w-4" />
                    <span>Valider</span>
                  </button>
                  
                  <button
                    onClick={handleRejectRetour}
                    className="bg-red-600 hover:bg-red-700 px-4 py-2 text-white text-sm rounded-lg font-medium transition-colors flex items-center space-x-2"
                  >
                    <X className="h-4 w-4" />
                    <span>Refuser</span>
                  </button>
                </>
              )}
              
              {canManageRetour && retour.status === 'en_attente' && (
                <Link
                  to={`/retours/${retour.id}/edit`}
                  className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Modifier"
                >
                  <Edit className="h-5 w-5" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Retour Info */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 sm:px-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informations retour</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut
                  </label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    retourService.getStatusColor(retour.status)
                  }`}>
                    {retourService.getStatusLabel(retour.status)}
                  </span>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chantier
                  </label>
                  <div className="flex items-center">
                    <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">
                      {chantierService.getChantierName(retour.chantierId)}
                    </span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Créé par
                  </label>
                  <div className="flex items-center">
                    <User className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">{retour.createdBy}</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de création
                  </label>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">
                      {new Date(retour.createdAt).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>
                
                {retour.validatedBy && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Validé par
                    </label>
                    <div className="flex items-center">
                      <User className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{retour.validatedBy}</span>
                    </div>
                  </div>
                )}
                
                {retour.validatedAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date de validation
                    </label>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">
                        {new Date(retour.validatedAt).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {retour.reason && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Raison du retour
                  </label>
                  <p className="text-sm text-gray-900">{retour.reason}</p>
                </div>
              )}
            </div>
          </div>

          {/* Retour Items */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 sm:px-8 border-b border-gray-200/50">
              <h3 className="text-lg font-medium text-gray-900">
                Articles retournés ({retour.items.length})
              </h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Produit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantité
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prix unitaire
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valeur totale
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      État
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white/50 divide-y divide-gray-200">
                  {retour.items.map((item) => (
                    <tr key={item.productId} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {productService.getProductName ? productService.getProductName(item.productId) : item.productId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.unitPrice.toFixed(2)} €
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.totalValue.toFixed(2)} €
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          item.condition === 'bon' ? 'bg-green-100 text-green-800' :
                          item.condition === 'abime' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {item.condition === 'bon' ? 'Bon état' :
                           item.condition === 'abime' ? 'Abîmé' : 'Utilisable'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Retour Summary */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 border-b border-gray-200/50">
              <h3 className="text-lg font-medium text-gray-900">Résumé</h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Articles</span>
                <span className="text-sm font-medium text-gray-900">{retour.items.length}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Quantité totale</span>
                <span className="text-sm font-medium text-gray-900">
                  {retour.items.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex justify-between">
                  <span className="text-base font-medium text-gray-900">Valeur totale</span>
                  <span className="text-base font-bold text-gray-900">
                    {retour.totalValue.toFixed(2)} €
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 border-b border-gray-200/50">
              <h3 className="text-lg font-medium text-gray-900">Actions rapides</h3>
            </div>
            
            <div className="p-6 space-y-3">
              {canManageRetour && retour.status === 'en_attente' && (
                <>
                  <button
                    onClick={handleValidateRetour}
                    className="w-full btn-primary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    <Check className="h-4 w-4" />
                    <span>Valider le retour</span>
                  </button>
                  
                  <button
                    onClick={handleRejectRetour}
                    className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                  >
                    <X className="h-4 w-4" />
                    <span>Refuser le retour</span>
                  </button>
                </>
              )}
              
              <Link
                to={`/chantiers/${retour.chantierId}`}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
              >
                <Building2 className="h-4 w-4" />
                <span>Voir le chantier</span>
              </Link>
              
              <button className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2">
                <FileText className="h-4 w-4" />
                <span>Imprimer bon de retour</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RetourDetail;